/**
 * ConfirmationPages — the BOOKING CONFIRMATION PAGE library + designer, ported
 * 1:1 from the Gold Master prototype `Confirmation Pages.dc.html`.
 *
 * NAMING + SCOPE (2026-09-01, agreed with the API line). This authors the page a
 * customer reaches from the LINK in their confirmation email or SMS, at
 * manage.<tenant>/<booking id>, whenever they choose to open it. It is designed
 * PER SERVICE — hence pages claiming services, claiming stealing, and one tenant
 * default catching the rest. Do NOT confuse it with the **journey end page**
 * (`confirmation-sections.ts`, `NJPage.sections`), the last screen inside the
 * booking flow. The two are unrelated in the data model.
 *
 * A stage-5 plan to migrate and retire this screen was CANCELLED on 2026-09-01:
 * it rested on the two being one feature. This surface stays, and so do its
 * seven ops — the API line has built them, against this model, with each
 * assignment rule covered by a deliberate-breakage test.
 *
 * Route `confirmpages`, reached as the SECOND TAB of the Journeys section
 * (Journeys · Confirmation pages) — operator's placement, not its own rail item.
 * Every style literal below is the prototype's; like NewJourneys this surface is
 * deliberately prototype-styled and does NOT use the design system.
 *
 * What is REAL here:
 *  • The service list is the tenant's OWN general service catalogue
 *    (listStudioServices). Assignment is against real services, so a claim
 *    means something. An empty catalogue says so rather than inventing rows.
 *  • Pages, section order/visibility, branding and service assignments persist
 *    through `confirmation-pages-api` — ON THE CONTRACT since 2026-09-04. The
 *    seven admin operations are live, the tenant default is bootstrapped by the
 *    server, and drafts do not resolve. Every write is async with its own error
 *    path; the screen has a loading and a failed state because it can now have
 *    them.
 *
 * What is NOT real, and is called out on screen rather than faked:
 *  • The DESIGNER's canvas. It is a design preview built from representative
 *    booking values with an author-chosen page state — not a live booking. The
 *    customer's real page is a separate surface (`src/manage`), which calls
 *    `getPublicBookingConfirmationPage(bookingId)` and maps it through
 *    `manage/confirmation-page-view.ts`. Both render the SAME `CSCanvas`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Screen } from "../../shell/chrome";
import { useTenant } from "../../shell/access";
import { isApiError } from "@v0y4ge/api-client";
/* THE ASSIGNMENT TARGETS ARE THE TENANT'S GENERAL SERVICE CATALOGUE.
 *
 * `listStudioServices` — `GET /v1/admin/catalog/services` — the same catalogue
 * the Services screen administers. Two earlier sources were wrong in different
 * ways: the workforce ops catalogue (an operation no worker serves, whose 404
 * the fetch's catch turned into "the catalogue is empty" on every live tenant),
 * then `listAdminAppointmentServices`, which serves only the APPOINTMENT
 * catalogue.
 *
 * The appointment list is a subset, and that is the whole defect: a confirmation
 * page is authored per service for every service a tenant sells, not only the
 * ones booked as appointments. A service outside that subset could not be
 * claimed, did not appear in the filter, was missing from a page's assignment
 * names, and — worst, because it is silent — was absent from the default page's
 * "N services fall back here" count. The count read as complete and was not.
 *
 * One source fixes all four, because the picker, the library filter, the
 * per-page names and the fallback counts are all derived from `svcList` below.
 * Nothing here is an API change: the operation already exists and is already
 * served. */
import { listStudioServices } from "@v0y4ge/api-client";
import { studioRequestOptions } from "../../lib/studio-config";
import { toast } from "../../shell/toast";
/* The DESIGN SYSTEM's spinner, not a local one. A hand-rolled ring was the
   first draft here and it would have been the same mistake as this screen's
   bespoke toast: it handles prefers-reduced-motion, the on-brand-fill ink and
   the role="status" announcement, none of which a private copy would. */
import { Spinner } from "@v0y4ge/jrni-ui";
type CatalogueService = { id: string; name: string };
/** Runaway guard on the catalogue read. At production's 100-per-page cap this is
 *  5,000 services — far past any real tenant, so reaching it means the server is
 *  not terminating rather than that the tenant is enormous. Either way the read
 *  has failed and is reported as a failure. */
const MAX_SERVICE_PAGES = 50;
import {
  listConfirmationPages, putConfirmationPage, cloneConfirmationPage,
  deleteConfirmationPage, setPageServices, resolveLocally, CP_STUBBED,
  type CPDoc, type CPApplies, type CPBlock,
} from "./confirmation-pages-api";
import {
  csAt, csToggle, csDef,
  type CSState,
} from "./confirmation-sections";
import {
  CSRail, CSCanvas, CSInspector, confirmationBlockHref, safeHttpHref,
  type CSBooking, type CSPageState,
} from "./confirmation-page";
import type { CSType } from "./confirmation-sections";
import {
  useCanvasWidth, CanvasColumn, CanvasWidthBar, CanvasEdge,
} from "../shared/editor-shell";
import { BlockCanvas, BLOCK_ADD_DND } from "./block-canvas";
import ConfirmationPagePreview from "./ConfirmationPagePreview";

/* ── Prototype style literals ───────────────────────────────────────── */
const FONT = '"DM Sans", "Helvetica Neue", sans-serif';
const CAPS = { fontSize: 10.5, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase" as const, color: "#697180" };
const INPUT = { width: "100%", boxSizing: "border-box" as const, height: 32, padding: "0 10px", border: "1px solid #D1D4DC", borderRadius: 7, fontSize: 12.5, color: "#333333", outline: "none", background: "#FFFFFF", fontFamily: FONT };
const GHOST = { height: 34, padding: "0 14px", border: "1px solid #D1D4DC", borderRadius: 8, background: "#FFFFFF", color: "#333333", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: FONT, flex: "none" } as const;
const PRIMARY = { height: 34, padding: "0 16px", border: "none", borderRadius: 8, background: "#5142C7", color: "#FFFFFF", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: FONT, flex: "none" } as const;
const ROW_COLS = "1fr 120px 1fr 96px 96px 40px";
const NOTE_BLUE = { padding: "11px 12px", borderRadius: 8, background: "#E8F1FD", border: "1px solid #C7DDF7", fontSize: 11.5, color: "#1B4C9E", lineHeight: 1.6 };

function ensureFont() {
  if (document.getElementById("cp-dm-sans")) return;
  const l = document.createElement("link");
  l.id = "cp-dm-sans"; l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,700;9..40,800&display=swap";
  document.head.appendChild(l);
}

/* ── Section registry ─────────────────────────────────────────────────
   ONE registry, shared with the journey end page (2026-09-02, convergence
   step 2). This file used to carry its own copy of the same sections, in its
   own words — two registries describing one set of ideas, which had already
   drifted by three sections and a dozen help strings. `confirmation-sections.ts`
   is now the only place a section is defined, and both designers read it.

   The three that lived only here — What to bring, Live queue position, Feedback
   prompt — moved into it with their journey types. Feedback prompt carries the
   new `afterTheFact` flag: it renders on THIS page, opened days later, and never
   on the journey end page, because it cannot ask how it went while the customer
   is still standing in the flow. That flag is the surface difference between the
   two pages, stated once, rather than two registries that quietly disagree.

   As of convergence step 3 this file does not read the registry directly at all:
   the rail, the canvas and the section inspector are `CSRail` / `CSCanvas` /
   `CSInspector` from `confirmation-page.tsx`, the same three the journey
   designer mounts. Tabs, a width control and a blocks rail existed TWICE after
   2026-09-01 — which made the duplication the convergence exists to remove
   larger, not smaller. This is that reversed. */
const CP_SURFACE = "linked" as const;

/* Canvas width: the Journey Designer's control, verbatim — the Desktop / Mobile
   toggle to seed it, a row of named width presets, and a drag bar either side of
   the canvas. Restored together on 2026-09-02 after the operator pointed out that
   the toggle alone cannot check anything between 440px and "as wide as the pane":
   there was no way to sweep the breakpoints and watch the page reflow. The drag
   is never persisted — it is a check that the layout holds, not a property of the
   page.

   The 372/720/1080 pills this replaces were three unexplained numbers with no
   handles; these are the shared shell's, so both designers move together. */
const WIDTH_PRESETS = [
  { w: 440, label: "Phone" },
  { w: 768, label: "Tablet" },
  { w: 1180, label: "Laptop" },
  { w: 4000, label: "Full" },
];

/* Blocks are NOT sections. A section renders the booking; a block holds copy the
   author types. Same distinction, same wording, as the Journey Designer. */
const BLOCK_META: Record<string, { label: string; glyph: string; hint: string; text: string }> = {
  heading:   { label: "Heading", glyph: "H", hint: "A line of your own, above the copy that follows.", text: "Before you arrive" },
  text:      { label: "Text", glyph: "¶", hint: "A paragraph in your own words.", text: "Parking is free for the first two hours — bring your ticket to reception and we'll validate it." },
  callout:   { label: "Callout", glyph: "!", hint: "A tinted box for something that must not be missed.", text: "Bring photo ID. We can't complete the appointment without it." },
  link:      { label: "Link", glyph: "↗", hint: "One labelled link out.", text: "Read the mortgage checklist" },
  divider:   { label: "Divider", glyph: "—", hint: "A rule between two runs of content.", text: "" },
};
const BLOCK_IDS = Object.keys(BLOCK_META);
let blockSeq = 0;
const mkBlock = (type: string): CPBlock => ({
  uid: `bl-${Date.now().toString(36)}-${++blockSeq}`,
  type,
  text: BLOCK_META[type]?.text ?? "",
  ...(type === "link" ? { href: "" } : {}),
});

type LinkIssue = { uid: string; field: "label" | "href"; message: string };

const ownsHref = (block: CPBlock): boolean => Object.prototype.hasOwnProperty.call(block, "href");

/** The value shown in the destination control. Legacy URL-in-text documents
 * stay valid and become a labelled link naturally when their label is edited. */
const linkDestination = (block: CPBlock): string => {
  if (ownsHref(block)) return typeof block.href === "string" ? block.href : "";
  return safeHttpHref(String(block.text ?? "")) ?? "";
};

const linkIssue = (block: CPBlock): LinkIssue | null => {
  if (block.type !== "link") return null;
  if (!String(block.text ?? "").trim()) {
    return { uid: block.uid, field: "label", message: "Enter a link label." };
  }
  const destination = linkDestination(block);
  if (!destination.trim()) {
    return { uid: block.uid, field: "href", message: "Enter a destination URL." };
  }
  if (!safeHttpHref(destination)) {
    return { uid: block.uid, field: "href", message: "Enter a full URL beginning with http:// or https://." };
  }
  return null;
};

const firstLinkIssue = (doc: CPDoc): LinkIssue | null => {
  for (const block of doc.content ?? []) {
    const issue = linkIssue(block);
    if (issue) return issue;
  }
  return null;
};
const STATES = [
  { value: "confirmed", label: "Confirmed", hint: "the happy path" },
  { value: "cancelled", label: "Cancelled", hint: "customer or branch cancelled" },
  { value: "rescheduled", label: "Rescheduled", hint: "moved, new time shown" },
  { value: "past", label: "Past", hint: "already attended" },
  { value: "expired", label: "Link expired", hint: "invalid or expired token" },
  { value: "closed", label: "Window closed", hint: "too late to change" },
];
const APPLIES_LABEL: Record<CPApplies, string> = { all: "All bookings", appointment: "Appointment", event: "Event", queue: "Queue ticket" };
const APPLIES_TINT: Record<CPApplies, { tint: string; color: string }> = {
  all: { tint: "#E8F1FD", color: "#1B58B8" },
  appointment: { tint: "#EDE5FE", color: "#5142C7" },
  event: { tint: "#FDF0E3", color: "#B9770E" },
  queue: { tint: "#E3F5E9", color: "#1D8348" },
};

/* `seedDefault` is deleted (2026-09-04, the seam swap). The server bootstraps
   the tenant default on first read, so the list is never empty — and `isDefault`
   is server-owned and IGNORED on write, so a client-side seed would have created
   a NON-default page named "Default confirmation page": a decoy fallback that
   resolves for nothing. Deleted rather than guarded. */


/** An error's own words when it has them, the caller's when it does not. The
 *  BRANCH is always `e.code` — never the message text, which is prose and
 *  changes. */
function errText(e: unknown, fallback: string): string {
  if (isApiError(e)) {
    if (e.code === "CONFIRMATION_PAGE_DEFAULT_CONFLICT") return "The default page is structural and cannot be removed.";
    if (e.code === "CONFIRMATION_PAGE_NOT_FOUND") return "That page no longer exists — someone may have deleted it.";
    if (e.code === "NETWORK_ERROR") return "No answer from the server. Nothing was changed.";
    if (e.message) return e.message;
  }
  return fallback;
}

/** The server sends an ISO timestamp; the column shows how long ago. Absolute
 *  time stays on the `title`, because "3 days ago" is not something an author
 *  can quote back to anyone. */
function fmtUpdated(iso: string): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  const mins = Math.round((Date.now() - t) / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days} d ago`;
  return new Date(t).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

/** A page's `applies` mapped onto the registry's journey type. `all` is the
 *  tenant default, which serves every kind of booking — appointment is the
 *  widest section set of the three, so it is what the designer previews. */
const csTypeFor = (a: CPApplies) => (a === "event" ? "event" : a === "queue" ? "queue" : "appointment");

export default function ConfirmationPages() {
  const tenant = useTenant();
  const slug = tenant?.slug || "default";

  const [route, setRoute] = useState<"library" | "designer">("library");
  const [pages, setPages] = useState<CPDoc[]>([]);
  const [assignments, setAssignments] = useState<Record<string, string>>({});
  const [services, setServices] = useState<CatalogueService[] | null>(null);
  const [servicesError, setServicesError] = useState<string | null>(null);
  const [servicesAttempt, setServicesAttempt] = useState(0);
  /** Baseline for the dirty check — the document as last read from the server.
   *  Compared structurally rather than tracked with a flag: a flag has to be set
   *  at every mutation site and is wrong the moment one is added, and this
   *  screen patches the document from a rail, a canvas, an inspector and three
   *  setup panes. It also makes an edit-and-undo correctly NOT dirty. */
  const baselineRef = useRef<string>("");

  /* library controls */
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** WHICH request is in flight, not merely that one is. A single boolean can
   *  only grey every button at once, which tells an author that something is
   *  happening but not what — and Save and Publish sit next to each other. */
  const [busyKind, setBusyKind] = useState<null | "save" | "publish" | "services" | "create">(null);
  /** STAGED service claims for the open page — null means "no local edits, use
   *  the server's".
   *
   *  Each tick used to fire `setPageServices` + a full `reload()`. On the mock
   *  that is instant; on a real tenant it is a round trip per checkbox, against
   *  a write that REASSIGNS — so an author claiming six services made six
   *  sequential writes, each stealing and each re-reading the whole library,
   *  and the list visibly lagged their clicking. Worse than slow: the writes
   *  interleave, so a fast author could leave the server holding a set they
   *  never asked for.
   *
   *  Ticking is now local and one Save writes the final set. That also makes
   *  the operation match what it is — a page claims a SET of services, and the
   *  op takes a set — instead of simulating it with a burst of writes. */
  const [svcDraft, setSvcDraft] = useState<string[] | null>(null);
  const [filter, setFilter] = useState<"all" | CPApplies>("all");
  const [svcFilter, setSvcFilter] = useState("all");
  const [sort, setSort] = useState("updated");
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [hoverFor, setHoverFor] = useState<string | null>(null);
  const inMenu = useRef(false);

  /* designer state */
  const [doc, setDoc] = useState<CPDoc | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [pageState, setPageState] = useState("confirmed");
  /** Selected SECTION row uid, or null. A uid rather than a section id because
   *  that is what `CSState` rows are keyed by and what survives a reorder. */
  const [selSection, setSelSection] = useState<string | null>(null);
  /** uid of the selected BLOCK, or null when a section is selected. The two rails
   *  share one inspector, so exactly one of these is live at a time. */
  const [selBlock, setSelBlock] = useState<string | null>(null);
  /** Page / Brand / Services open as their OWN PANE (operator, 2026-09-02),
   *  the way the Journey Designer's Setup views do — they configure the page,
   *  and a page-wide setting in the panel that is otherwise showing the selected
   *  section put two scopes in one place. null = the three-pane designer. */
  const [setupView, setSetupView] = useState<null | "page" | "branding" | "services">(null);
  /** The editor panel is an OVERLAY that can be minimized, not a fixed column
   *  (2026-09-02). As three fixed panes — 252 rail + flex canvas + 300 panel —
   *  the canvas got 332px of a 924px window and the page preview rendered as a
   *  226px sliver with its own scrollbar. The Journey Designer survives the same
   *  window because its rail is 212 and its inspector overlays the canvas; this
   *  now does both. */
  const [inspOpen, setInspOpen] = useState(true);
  /* The editor overlay reserves its width only when the row can afford it.
     Measured with a ResizeObserver rather than a layout effect on every commit:
     a per-commit measure can OSCILLATE here, because the reservation changes the
     canvas pane's padding, which reflows its content, which can add or remove a
     scrollbar, which changes the measured width, which flips the reservation
     back — React error #185, maximum update depth. The observer only fires on a
     real box change, and the 12px tolerance below swallows a scrollbar's worth
     of jitter so the decision cannot chatter across its own threshold. */
  const [rowW, setRowW] = useState(0);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const rowRoRef = useRef<ResizeObserver | null>(null);
  const rowRefCb = useCallback((el: HTMLDivElement | null) => {
    if (rowRoRef.current) { rowRoRef.current.disconnect(); rowRoRef.current = null; }
    rowRef.current = el;
    if (!el) return;
    const read = () => {
      const w = Math.round(el.getBoundingClientRect().width);
      /* Tolerance, not equality: without it a 15px scrollbar appearing is a
         width change, and the width change is what flips the reservation. */
      if (w) setRowW((prev) => (Math.abs(prev - w) < 12 ? prev : w));
    };
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(read);
      ro.observe(el);
      rowRoRef.current = ro;
    }
    read();
  }, []);
  useEffect(() => () => { if (rowRoRef.current) rowRoRef.current.disconnect(); }, []);
  const INSP_MAX = 420;
  /** Canvas width left if the panel reserved its full width. */
  const canvasIfReserved = rowW - 212 - 40 - INSP_MAX;
  /** Can the row afford the panel BESIDE the canvas? 420 is the floor — below
   *  that the preview stops being a preview. */
  const reserveInsp = rowW > 0 && canvasIfReserved >= 420;
  /** Never wider than what leaves a readable strip of canvas beside it. Gating
   *  only the padding — the first version of this — let a 420px panel open over
   *  a 414px canvas and occlude 90% of it, which is worse than the narrow canvas
   *  it was meant to fix: the panel showed "Nothing selected" on top of the page
   *  it was a placeholder for. */
  const inspW = rowW > 0 ? Math.max(260, Math.min(INSP_MAX, rowW - 212 - 300)) : INSP_MAX;
  const [svcQuery, setSvcQuery] = useState("");
  /* `dlOff` retired 2026-09-14: it was component state, so every download
     toggle was lost on reload and never reached the page. The four items now
     live on the document as `downloadsOff` and the canvas honours them. */
  /* Hook, so it lives at the component's top level rather than inside Designer
     — a hook called from a nested function component would change the hook
     order the moment the route changed. */
  /* Seeded at Full; the width bar's presets and the drag bars are the control.
     A Desktop / Mobile pair in the header was removed on 2026-09-02 (operator):
     with the preset row directly below it, two controls answered one question,
     and "Desktop" was the same thing as the Full preset under another name. */
  const pageWidth = useCanvasWidth({ initial: 4000, min: 320, max: 4000 });

  useEffect(() => { ensureFont(); }, []);
  /* Selecting anything opens the editor if it was minimized — the journey
     designer's rule. Keyed on the SELECTION, not on `inspOpen`, so minimizing
     while something stays selected does not immediately undo itself. */
  useEffect(() => {
    /* Only where the panel sits BESIDE the canvas. Where it overlays, reopening
       on selection hides the section that was just selected — and the Move up /
       Move down / Remove controls are on the canvas, so it would hide the
       controls the selection exists to reach. There the author opens the editor
       deliberately, from the tab. */
    if (!reserveInsp) return;
    if (selSection !== null || selBlock !== null) setInspOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selSection, selBlock, reserveInsp]);
  /* Shut it when the row stops being able to afford it. Once, on the transition,
     so a deliberate open on a narrow row is not fought by this. */
  const affordedRef = useRef(true);
  useEffect(() => {
    if (rowW === 0) return;
    if (affordedRef.current && !reserveInsp) setInspOpen(false);
    affordedRef.current = reserveInsp;
  }, [reserveInsp, rowW]);

  /** The PLATFORM toast, not this screen's own card (2026-09-10).
   *
   *  This used to render a bespoke fixed-position panel at top-right, so save,
   *  preview and publish here announced themselves in a different place, shape
   *  and colour from every other screen in Studio — which is exactly how a
   *  surface stops reading as part of the product. `toast()` is the shell's
   *  dispatcher over the design system's Toast + ToastViewport, mounted once in
   *  App, and it owns placement, stacking, dismissal and timing.
   *
   *  `say` is kept as the local name so the fifteen call sites stay put, and it
   *  infers the tone rather than making each one pass it: every failure on this
   *  screen is phrased "Not saved" / "Not created" / "Not assigned", so the
   *  negative prefix IS the severity. A call can still override. */
  const say = (title: string, sub: string, tone?: "success" | "error" | "info") => {
    toast({
      tone: tone || (/^(Not|Couldn|Failed)\b/.test(title) ? "error" : "success"),
      title,
      subtitle: sub,
    });
  };

  /* The seam is async now, so this screen has three states it could not have
     before: loading, loaded, and failed. A screen that cannot fail is a screen
     lying about a network. */
  const reload = async () => {
    try {
      const res = await listConfirmationPages(slug);
      setPages(res.pages); setAssignments(res.assignments);
      setLoadErr(null);
    } catch (e) {
      setLoadErr(errText(e, "The confirmation pages could not be loaded."));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { setLoading(true); void reload(); }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  /* The tenant's general service catalogue — every assignment target.
   *
   * PAGED, and read to the end. The response carries `hasMore`, so stopping at
   * page one would silently truncate on any tenant past the page size — and a
   * truncated catalogue here does not look like an error, it looks like a
   * smaller tenant: services quietly missing from the picker and, worse,
   * missing from a default page's fallback count. `hasMore` is the server's own
   * answer to "is there more", so it is what decides, not a short page.
   *
   * The ceiling is a runaway guard against a server that always answers
   * hasMore. Reaching it is a FAILURE, not a stopping point: the first version
   * of this published whatever had accumulated, which at production's 100-per-
   * page cap would have presented 5,000 services as the whole catalogue — the
   * same silently-short list this paging exists to prevent, just further along.
   * So the list is published ONLY after pagination actually completes, and an
   * exhausted guard falls into the same error-and-retry state as a failed
   * request. A catalogue that cannot be read in full is not a smaller
   * catalogue. */
  useEffect(() => {
    let live = true;
    setServices(null);
    setServicesError(null);
    const opts = studioRequestOptions("catalog") as { baseUrl: string };
    void (async () => {
      try {
        const acc: CatalogueService[] = [];
        /* 100 matches production's per-page cap; asking for more just gets 100. */
        let more = true;
        for (let page = 1; more && page <= MAX_SERVICE_PAGES; page++) {
          const res = await listStudioServices({ page: String(page), limit: "100" }, opts);
          if (!live) return;
          /* `id`, `serviceId` and `service_id` are the SAME canonical id in three
             spellings — the contract carries all three for byte-identical parity
             with the existing backend rows. `serviceId` is read first because it
             is the one named for what it identifies; `id` is the fallback rather
             than the primary so that a row which ever stops echoing the alias
             still resolves. Whichever answers, the value is canonical, which is
             what keeps already-stored assignments pointing at the same services. */
          for (const row of res.services || res.items || []) {
            acc.push({ id: row.serviceId || row.id, name: row.name });
          }
          more = !!res.hasMore;
        }
        if (!live) return;
        /* Still more to read after the ceiling — refuse the list rather than
           show a prefix of it. `acc` is discarded on purpose. */
        if (more) {
          setServicesError(`The service catalogue is larger than this screen reads in one go (over ${MAX_SERVICE_PAGES * 100} services). Nothing is shown rather than part of it. Please try again, and tell an administrator if it keeps happening.`);
          return;
        }
        setServices(acc);
      } catch (e: unknown) {
        if (!live) return;
        setServicesError(isApiError(e) && e.status === 403
          ? "You do not have permission to view the service catalogue. Ask an administrator for access."
          : isApiError(e) && e.status === 401
            ? "Your session has expired. Sign in again to view the service catalogue."
            : "The service catalogue could not be loaded. Please try again.");
      }
    })();
    return () => { live = false; };
  }, [slug, servicesAttempt]);

  /* Click-away for the ⋯ menu. React delegates to the root container, so its
     synthetic handlers run before this document listener — the flag an in-menu
     handler sets is already true by the time we read it. */
  useEffect(() => {
    const away = () => {
      if (inMenu.current) { inMenu.current = false; return; }
      setMenuFor(null);
    };
    document.addEventListener("click", away);
    return () => { document.removeEventListener("click", away); };
  }, []);

  const svcList = services || [];
  const matchingServices = svcList.filter((s) => !svcQuery.trim() || s.name.toLowerCase().includes(svcQuery.trim().toLowerCase()));
  const defaultPage = pages.find((p) => p.isDefault) || null;

  /* A page "serves" a service if it claims it, or — for the default page — if
     nothing else claims it. Same rule as resolveForService, so the list filter
     cannot disagree with the runtime. */
  /* `resolveLocally` is the seam's own resolution rule, computed from the list
     this screen already holds — so the column, the filter and the server cannot
     disagree. It also excludes DRAFT pages from claiming, which the previous
     inline `assignments[sv.id] === p.id` did not: a draft page that claims a
     service still renders the default, and the column now says so. */
  const namesFor = (p: CPDoc) => svcList
    .filter((sv) => resolveLocally(pages, assignments, sv.id)?.id === p.id)
    .map((sv) => sv.name);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const serves = (p: CPDoc) => svcFilter === "all" || resolveLocally(pages, assignments, svcFilter)?.id === p.id;
    const out = pages
      /* The third clause was `|| (p.isDefault && filter === "all")` — unreachable,
         because the first clause already returns true for every row when the
         filter is "all". It read as "the default page is always listed", which is
         a DIFFERENT rule and is not what it did; if that rule is wanted it is
         `|| p.isDefault` with no filter test. Removed rather than corrected,
         because the library has never behaved that way (2026-09-08). */
      .filter((p) => filter === "all" || p.applies === filter)
      .filter(serves)
      .filter((p) => !q || p.name.toLowerCase().includes(q) || p.sub.toLowerCase().includes(q)
        || namesFor(p).some((n) => n.toLowerCase().includes(q)));
    if (sort === "name") out.sort((a, b) => (a.isDefault === b.isDefault ? a.name.localeCompare(b.name) : a.isDefault ? -1 : 1));
    if (sort === "assigned") out.sort((a, b) => (a.isDefault === b.isDefault ? namesFor(b).length - namesFor(a).length : a.isDefault ? -1 : 1));
    return out;
  }, [pages, assignments, query, filter, svcFilter, sort, services]);

  /** Snapshot the document as the server holds it. Section rows and branding are
   *  cloned on open, so a shallow reference check would never fire. */
  const snapshot = (p: CPDoc) => JSON.stringify({ ...p, updated: "" });

  const openDoc = (p: CPDoc, opts?: { fresh?: boolean }) => {
    /* No migration here: every read out of `confirmation-pages-api` is already
       normalized, so this screen never sees the legacy `{order, off}` shape.
       Migrating in this function alone was the 2026-09-02 defect — `clone`
       reads a stored page without opening it. */
    setDoc({ ...p, sections: p.sections.map((r) => ({ ...r })), branding: { ...p.branding } });
    baselineRef.current = snapshot(p);
    setSvcDraft(null);
    setRoute("designer");
    setSelSection(null); setSelBlock(null); setPageState("confirmed");
    /* A NEW page opens on Page; an existing one opens on Design (operator,
       2026-09-10). A clone arrives called "Copy of …", claiming nothing, and
       naming it is the first thing that has to happen — landing on the canvas
       invites an author to design a page they have not yet said what it is FOR,
       and "Applies to" decides which sections the rail even offers. Reopening an
       existing page on Page would be the opposite mistake: its identity is
       settled and the work is the design. */
    setSetupView(opts?.fresh ? "page" : null);
  };

  const patch = (p: Partial<CPDoc>) => setDoc((d) => (d ? { ...d, ...p } : d));

  /** Persist, then confirm — in that order. `okMsg` is said only after the write
   *  has actually landed: the old call sites fired "Saved" beside a synchronous
   *  call, and kept doing so against an async one would have claimed success
   *  while the request was still in flight, and again when it failed. */
  const save = async (next?: CPDoc, okMsg?: string, kind: "save" | "publish" = "save") => {
    const d = next || doc;
    if (!d) return;
    /* `next` can differ from the document currently on screen (Publish sets
       status before writing). Capture the displayed state that this request is
       saving over so a normalized response can be adopted when nothing else
       changed, while edits made during the request remain local and dirty. */
    const before = doc && doc.id === d.id ? snapshot(doc) : snapshot(d);
    const issue = firstLinkIssue(d);
    if (issue) {
      setSelBlock(issue.uid); setSelSection(null); setSetupView(null); setInspOpen(true);
      say(kind === "publish" ? "Not published" : "Not saved", issue.message, "error");
      return;
    }
    setBusy(true); setBusyKind(kind);
    try {
      const stored = await putConfirmationPage(slug, d);
      baselineRef.current = snapshot(stored);
      setDoc((current) => {
        if (!current || current.id !== d.id) return current;
        if (snapshot(current) === before) {
          return { ...stored, sections: stored.sections.map((r) => ({ ...r })), branding: { ...stored.branding } };
        }
        /* Inputs remain usable while the request is in flight. Keep edits made
           after this request started, while accepting the server-owned status
           and timestamp; comparing them with `baselineRef` leaves them dirty. */
        return { ...current, status: stored.status, updated: stored.updated, isDefault: stored.isDefault };
      });
      await reload();
      if (okMsg) say(kind === "publish" ? "Published" : d.status === "Published" ? "Live changes saved" : "Draft saved", okMsg);
    } catch (e) {
      say("Not saved", errText(e, "The page could not be saved. Nothing was changed."));
    } finally {
      setBusy(false); setBusyKind(null);
    }
  };

  /* ── library ──────────────────────────────────────────────────────── */

  function Library() {
    const claimed = svcList.filter((s) => assignments[s.id]).length;
  /* Every service-derived COUNT is unknown until the catalogue is read in full.
     `svcList` is [] while loading and [] after a failed or guard-exhausted read,
     so a count taken from it would render a confident "0 services fall back
     here" — a wrong total presented as a right one, which is the same failure
     as publishing a truncated list, just with a different number. An em dash
     says "not known yet"; zero says "none". */
  const svcCountsKnown = services !== null;
  const svcCount = (n: number) => (svcCountsKnown ? String(n) : "—");
    const tiles = [
      { glyph: "▤", tint: "#EDE5FE", color: "#5142C7", value: String(pages.length), label: "Confirmation pages" },
      { glyph: "⌖", tint: "#E8F1FD", color: "#1B58B8", value: svcCount(claimed), label: "Services with a custom page" },
      { glyph: "◈", tint: "#E3F5E9", color: "#1D8348", value: svcCount(svcList.length - claimed), label: "Falling back to default" },
      { glyph: "◷", tint: "#FDF0E3", color: "#B9770E", value: String(pages.filter((p) => p.status === "Draft").length), label: "Drafts" },
    ];
    const chip = (on: boolean) => ({ borderColor: on ? "#5142C7" : "#D1D4DC", background: on ? "#F6F2FE" : "#FFFFFF", color: on ? "#5142C7" : "#5A6070" });

    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, fontFamily: FONT, background: "#F4F5F7" }}>
        <div style={{ background: "#FFFFFF", borderBottom: "1px solid #E6E8EC", flexShrink: 0, padding: "20px 24px 16px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0, letterSpacing: "-0.01em" }}>Confirmation pages</h1>
              <p style={{ fontSize: 12.5, color: "#697180", margin: "4px 0 0", lineHeight: 1.5, maxWidth: 760 }}>
                What a customer sees after booking, and where they cancel, reschedule or download the details.
                One default serves everything; clones take over named services.
              </p>
            </div>
            <button type="button" style={GHOST} onClick={() => say("Export queued", "A CSV of every confirmation page and its assigned services.")}>↓ Export</button>
            <button type="button" style={{ ...PRIMARY, display: "inline-flex", alignItems: "center", gap: 7 }} onClick={async () => {
              if (!defaultPage || busy) return;
              setBusy(true); setBusyKind("create");
              try {
                const c = await cloneConfirmationPage(slug, defaultPage.id);
                await reload();
                if (c) { openDoc(c, { fresh: true }); say("Started from the default", "New pages are clones of the default, so every section is guaranteed to exist. It is live and claims no services — assign them on the Services tab."); }
              } catch (e) {
                say("Not created", errText(e, "The page could not be created."));
              } finally { setBusy(false); setBusyKind(null); }
            }}>
              {busyKind === "create" ? <><Spinner size="sm" onColor label="Creating" /> Creating…</> : "+ New confirmation page"}
            </button>
          </div>
        </div>

        <div style={{ flex: 1, overflow: "auto", minHeight: 0 }}>
          <div style={{ padding: "16px 24px 0", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12 }}>
            {tiles.map((t) => (
              <div key={t.label} style={{ background: "#FFFFFF", border: "1px solid #E6E8EC", borderRadius: 10, padding: "12px 14px", display: "flex", alignItems: "center", gap: 11 }}>
                <span style={{ width: 30, height: 30, borderRadius: 8, background: t.tint, color: t.color, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13, flex: "none" }}>{t.glyph}</span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 19, fontWeight: 700, lineHeight: 1.15 }}>{t.value}</span>
                  <span style={{ display: "block", fontSize: 11, color: "#697180", whiteSpace: "nowrap" }}>{t.label}</span>
                </span>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "16px 24px 12px", flexWrap: "wrap" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 7, flex: "0 1 260px", minWidth: 220, boxSizing: "border-box", height: 32, padding: "0 11px", border: "1px solid #D1D4DC", borderRadius: 8, background: "#FFFFFF" }}>
              <span style={{ color: "#B5BAC6", fontSize: 12 }}>⌕</span>
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a page by name or service"
                style={{ flex: 1, minWidth: 0, border: "none", outline: "none", fontSize: 12.5, background: "transparent", color: "#333333", fontFamily: FONT }} />
            </span>
            <span style={{ width: 1, height: 20, background: "#E6E8EC", margin: "0 4px" }} />
            {([["all","All"],["appointment","Appointments"],["event","Events"],["queue","Queue tickets"]] as const).map(([k, label]) => (
              <button key={k} type="button" onClick={() => setFilter(k as "all" | CPApplies)}
                style={{ height: 28, padding: "0 11px", border: "1px solid", borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", flex: "none", fontFamily: FONT, ...chip(filter === k) }}>{label}</button>
            ))}
            <span style={{ flex: "1 0 0", minWidth: 0 }} />
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flex: "none", height: 28, padding: "0 10px", border: `1px solid ${svcFilter === "all" ? "#D1D4DC" : "#5142C7"}`, borderRadius: 8, background: svcFilter === "all" ? "#FFFFFF" : "#F6F2FE", fontSize: 12, fontWeight: 600, color: svcFilter === "all" ? "#333333" : "#5142C7" }}>
              <span style={{ color: "#697180", fontWeight: 500 }}>Service</span>
              <select value={svcFilter} onChange={(e) => setSvcFilter(e.target.value)}
                style={{ border: "none", background: "transparent", fontSize: 12, fontWeight: 600, color: "inherit", cursor: "pointer", outline: "none", maxWidth: 170, fontFamily: FONT }}>
                <option value="all">Any service</option>
                {svcList.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flex: "none", height: 28, padding: "0 10px", border: "1px solid #D1D4DC", borderRadius: 8, background: "#FFFFFF", fontSize: 12, fontWeight: 600 }}>
              <span style={{ color: "#697180", fontWeight: 500 }}>Sort</span>
              <select value={sort} onChange={(e) => setSort(e.target.value)} style={{ border: "none", background: "transparent", fontSize: 12, fontWeight: 600, color: "inherit", cursor: "pointer", outline: "none", fontFamily: FONT }}>
                <option value="updated">Last updated</option>
                <option value="name">Name</option>
                <option value="assigned">Services assigned</option>
              </select>
            </span>
          </div>

          <div style={{ padding: "0 24px 24px" }}>
            {/* Three states, said plainly. The failure names what could not be done
                and what was not lost by it — never a bare code, and never a silent
                empty table that reads as "this tenant has no pages". */}
            {loadErr ? (
              <div style={{ background: "#FFFFFF", border: "1px solid #F3C6D2", borderRadius: 12, padding: "18px 20px" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#D01647" }}>The confirmation pages could not be loaded</div>
                <div style={{ fontSize: 12, color: "#697180", marginTop: 4, lineHeight: 1.6 }}>{loadErr} Nothing has been changed.</div>
                <button type="button" style={{ ...GHOST, marginTop: 12 }} onClick={() => { setLoading(true); void reload(); }}>Try again</button>
              </div>
            ) : loading ? (
              <div style={{ background: "#FFFFFF", border: "1px solid #E6E8EC", borderRadius: 12, padding: "28px 20px", fontSize: 12.5, color: "#9AA1AE" }}>Loading confirmation pages…</div>
            ) : (
            <div style={{ background: "#FFFFFF", border: "1px solid #E6E8EC", borderRadius: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: ROW_COLS, gap: 12, alignItems: "center", padding: "9px 16px", background: "#FAFAFD", borderRadius: "11px 11px 0 0", borderBottom: "1px solid #E6E8EC", fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#697180" }}>
                <span>Confirmation page</span><span>Applies to</span><span>Assigned</span><span>Status</span><span>Updated</span><span />
              </div>
              {rows.map((p, i) => {
                const names = namesFor(p);
                const open = menuFor === p.id;
                const hov = hoverFor === p.id && names.length > 0;
                const flipUp = rows.length > 2 && i >= rows.length - 2;
                const tint = APPLIES_TINT[p.applies];
                return (
                  <div key={p.id} onClick={() => openDoc(p)}
                    style={{ position: "relative", display: "grid", gridTemplateColumns: ROW_COLS, gap: 12, alignItems: "center", padding: "13px 16px", borderBottom: "1px solid #F4F5F7", cursor: "pointer", background: open || hov ? "#FAFAFD" : "#FFFFFF", zIndex: open || hov ? 2 : 1 }}>
                    <span style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ width: 28, height: 28, borderRadius: 7, background: tint.tint, color: tint.color, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, flex: "none" }}>{p.isDefault ? "◈" : "▤"}</span>
                      <span style={{ minWidth: 0 }}>
                        <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: "#333333", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</span>
                          {p.isDefault ? <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: "0.06em", padding: "2px 7px", borderRadius: 999, background: "#E8F1FD", color: "#1B58B8", flex: "none" }}>DEFAULT</span> : null}
                        </span>
                        <span style={{ display: "block", fontSize: 11.5, color: "#697180", marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.sub}</span>
                      </span>
                    </span>
                    <span><span style={{ fontSize: 10.5, fontWeight: 700, padding: "3px 10px", borderRadius: 999, background: tint.tint, color: tint.color }}>{APPLIES_LABEL[p.applies]}</span></span>
                    <span onMouseEnter={() => setHoverFor(p.id)} onMouseLeave={() => setHoverFor((x) => (x === p.id ? null : x))}
                      style={{ position: "relative", fontSize: 12, color: "#697180", minWidth: 0 }}>
                      <span style={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", borderBottom: `1px dotted ${names.length ? "#C2C7D2" : "transparent"}` }}>
                        {!svcCountsKnown ? (servicesError ? "Service list unavailable" : "Counting services…")
                        : p.isDefault ? `${names.length} services fall back here`
                        : names.length ? `${names.length} service${names.length === 1 ? "" : "s"}`
                        : "No services yet"}
                      </span>
                      {hov ? (
                        <span style={{ position: "absolute", top: flipUp ? "auto" : "calc(100% + 6px)", bottom: flipUp ? "calc(100% + 6px)" : "auto", left: 0, zIndex: 40, minWidth: 230, maxWidth: 300, background: "#FFFFFF", border: "1px solid #E6E8EC", borderRadius: 10, boxShadow: "0 10px 28px rgba(38,34,101,0.16)", padding: "10px 12px" }}>
                          <span style={{ display: "block", ...CAPS, fontSize: 10, color: "#9AA1AE", marginBottom: 7 }}>{p.isDefault ? "Falling back to this page" : "Services on this page"}</span>
                          {names.slice(0, 6).map((n) => (
                            <span key={n} style={{ display: "flex", alignItems: "center", gap: 7, padding: "3px 0" }}>
                              <span style={{ width: 5, height: 5, borderRadius: 999, background: p.isDefault ? "#C2C7D2" : "#5142C7", flex: "none" }} />
                              <span style={{ fontSize: 12, color: "#333333", lineHeight: 1.45 }}>{n}</span>
                            </span>
                          ))}
                          {names.length > 6 ? <span style={{ display: "block", fontSize: 11, color: "#9AA1AE", marginTop: 6 }}>+{names.length - 6} more</span> : null}
                        </span>
                      ) : null}
                    </span>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: p.status === "Published" ? "#1D8348" : "#B9770E" }}>
                      <span style={{ width: 6, height: 6, borderRadius: 999, background: p.status === "Published" ? "#1D8348" : "#B9770E" }} />{p.status}
                    </span>
                    <span style={{ fontSize: 12, color: "#697180" }} title={p.updated || ""}>{fmtUpdated(p.updated)}</span>
                    <span style={{ position: "relative", display: "inline-flex", justifyContent: "flex-end" }}>
                      <span onClick={(e) => { e.stopPropagation(); inMenu.current = true; setMenuFor((x) => (x === p.id ? null : p.id)); }}
                        style={{ width: 26, height: 26, borderRadius: 6, color: open ? "#5142C7" : "#697180", background: open ? "#F6F2FE" : "transparent", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 14, cursor: "pointer" }}>⋯</span>
                      {open ? (
                        <span style={{ position: "absolute", top: flipUp ? "auto" : 30, bottom: flipUp ? 30 : "auto", right: 0, zIndex: 50, minWidth: 186, background: "#FFFFFF", border: "1px solid #E6E8EC", borderRadius: 10, boxShadow: "0 12px 32px rgba(38,34,101,0.18)", padding: 5, textAlign: "left" }}>
                          {[
                            { icon: "⧉", label: "Clone", hint: "Start a new page from this one", tone: "" },
                            { icon: "✎", label: "Edit", hint: "", tone: "" },
                            { icon: "↓", label: "Export", hint: "", tone: "" },
                            p.isDefault
                              ? { icon: "✕", label: "Delete", hint: "The default page cannot be deleted", tone: "off" }
                              : { icon: "✕", label: "Delete", hint: "", tone: "danger" },
                          ].map((mi) => (
                            <span key={mi.label} onClick={async (e) => {
                              e.stopPropagation(); inMenu.current = true;
                              if (mi.tone === "off") { say("The default page is structural", "Every booking no other page claims renders it, so it cannot be deleted."); return; }
                              setMenuFor(null);
                              if (mi.label === "Edit") { openDoc(p); return; }
                              if (mi.label === "Clone") {
                                try {
                                  const c = await cloneConfirmationPage(slug, p.id);
                                  await reload();
                                  /* The clone is LIVE, copying the source's status — not Draft as the
                                     stub made it. Said out loud, because "live" and "claims nothing"
                                     together are what make it safe: an author who assumed Draft
                                     would think they had parked something that is in fact published. */
                                  say("Cloned", c ? `${c.name} is live and claims no services yet — assign them on the Services tab.` : "Nothing to clone.");
                                } catch (err) { say("Not cloned", errText(err, "The page could not be cloned.")); }
                                return;
                              }
                              if (mi.label === "Delete") {
                                try {
                                  const res = await deleteConfirmationPage(slug, p.id);
                                  await reload();
                                  say(res.ok ? "Deleted" : "Not deleted", res.ok ? `${p.name} is gone. Its services fall back to the default page.` : "The default page is structural and cannot be removed.");
                                } catch (err) { say("Not deleted", errText(err, "The page could not be deleted.")); }
                                return;
                              }
                              say(`Export · ${p.name}`, "A JSON copy of the page document and its assignments.");
                            }}
                              style={{ display: "flex", alignItems: "center", gap: 9, padding: "7px 9px", borderRadius: 7, cursor: mi.tone === "off" ? "not-allowed" : "pointer", color: mi.tone === "danger" ? "#D01647" : mi.tone === "off" ? "#B5BAC6" : "#333333", fontSize: 12.5, fontWeight: 600 }}>
                              <span style={{ width: 14, textAlign: "center", color: mi.tone === "danger" ? "#D01647" : mi.tone === "off" ? "#C2C7D2" : "#9AA1AE", flex: "none" }}>{mi.icon}</span>
                              <span style={{ minWidth: 0 }}>
                                <span style={{ display: "block" }}>{mi.label}</span>
                                {mi.hint ? <span style={{ display: "block", fontSize: 10.5, fontWeight: 500, color: "#9AA1AE", lineHeight: 1.4, marginTop: 1 }}>{mi.hint}</span> : null}
                              </span>
                            </span>
                          ))}
                        </span>
                      ) : null}
                    </span>
                  </div>
                );
              })}
              {rows.length === 0 ? (
                <div style={{ padding: "40px 20px", textAlign: "center" }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700 }}>No pages match</div>
                  <div style={{ fontSize: 12, color: "#697180", marginTop: 4 }}>Clear the search or the service filter.</div>
                </div>
              ) : null}
            </div>
            )}
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 12, fontSize: 11.5, color: "#697180", lineHeight: 1.6 }}>
              <span style={{ flex: "none", width: 16, height: 16, borderRadius: 999, background: "#E8F1FD", color: "#1B58B8", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, marginTop: 1 }}>i</span>
              <span>
                The default page can be cloned and edited but never deleted — a service claimed by no other page renders it.
                {CP_STUBBED
                  ? " Pages and assignments are held in a per-tenant local stub: the seven confirmation-page ops have no contract yet, so nothing here reaches a backend."
                  : " Pages and assignments come from the confirmation-page operations. A Draft page claims nothing at runtime, and the tenant default is created by the server rather than here."}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── designer ─────────────────────────────────────────────────────── */

  function booking() {
    const a = doc?.applies || "appointment";
    if (a === "event") return { title: "Campus Open Day", when: "Saturday 12 September", time: "10:00 – 15:00", dur: "5 hours", svc: "General admission · 2 tickets" };
    if (a === "queue") return { title: "Branch visit", when: "Today", time: "Joined 14:12", dur: "~18 min wait", svc: "Open an account" };
    return { title: "Mortgage consultation", when: "Thursday 4 September", time: "14:30 – 15:15", dur: "45 minutes", svc: "Mortgage consultation" };
  }

  function Designer() {
    if (!doc) return null;
    const csType = csTypeFor(doc.applies);
    /* "All bookings" is not a fourth type — it is all three. The rail unions
       their sections so Guests and Live queue position can be placed on a page
       that genuinely serves events and queues; the canvas keeps ONE type,
       because a preview is of a booking and a booking has a kind. */
    const csScope: CSType[] = doc.applies === "all" ? ["appointment", "event", "queue"] : [csType];
    /* What the SERVER currently has this page claiming, and what the author has
       staged on top of it. This sits beside the document dirty check because
       both kinds of pending work feed the page-level status. */
    /* Assignments are authoritative even when the paged/filtered catalogue
       does not contain an assigned service. A set-shaped write must preserve
       every existing claim, including claims the current catalogue cannot
       render as a row. */
    const serverClaimed = () => Object.keys(assignments).filter((id) => assignments[id] === doc.id);
    const draftClaimed = svcDraft || serverClaimed();
    const svcAdded = draftClaimed.filter((id) => serverClaimed().indexOf(id) < 0);
    const svcRemoved = serverClaimed().filter((id) => draftClaimed.indexOf(id) < 0);
    const svcDirty = svcAdded.length > 0 || svcRemoved.length > 0;
    /* Unsaved / Published / Not published — the SAME three states, wording and
       semantic colours the Journey Designer's status pill uses, because an
       author moving between the two designers should not have to learn a second
       vocabulary for the same fact. Orange for anything not safely published,
       green only when what is on the server is what is on screen. */
    const dirty = snapshot(doc) !== baselineRef.current;
    const published = doc.status === "Published";
    const statusLabel = dirty || svcDirty ? "Unsaved changes" : published ? "Published" : "Not published";
    const settled = published && !dirty && !svcDirty;
    const statusInk = settled ? "var(--jrni-color-semantic-green-1)" : "var(--jrni-color-semantic-orange-1)";
    const statusFill = settled ? "var(--jrni-color-semantic-green-2)" : "var(--jrni-color-semantic-orange-2)";
    const state = doc.sections;
    const setSections = (next: CSState) => patch({ sections: next });
    const blocks = doc.content ?? [];
    const selBlockDoc = selBlock ? blocks.find((b) => b.uid === selBlock) ?? null : null;
    const selectedLinkIssue = selBlockDoc?.type === "link" ? linkIssue(selBlockDoc) : null;
    const selectedBlockKey = selBlockDoc ? selBlockDoc.uid.replace(/[^A-Za-z0-9_-]/g, "-") : "none";
    const selRow = selSection ? csAt(state, selSection) : null;
    const brand = doc.branding.inherit ? "#5142C7" : doc.branding.brandColor;
    const bg = doc.branding.inherit ? "#FFFFFF" : doc.branding.pageBg;
    const stateMeta = STATES.find((s) => s.value === pageState) || STATES[0];
    const orgName = tenant?.name || "Your organisation";
    const saveActionLabel = doc.status === "Published" ? "Save live changes" : "Save draft";

    const patchBlock = (uid: string, p: Partial<CPBlock>) =>
      patch({ content: blocks.map((b) => (b.uid === uid ? { ...b, ...p } : b)) });

    /* The sample booking the shared canvas renders against. Built HERE because
       the host owns the sample — the canvas invents nothing — and shaped by the
       page state, which is this surface's own axis: a page opened from an email
       weeks later can be cancelled, past or inside its change window, none of
       which the journey end page can be. */
    const b = booking();
    const csBooking: CSBooking = {
      brandName: orgName,
      serviceName: b.svc,
      eventName: doc.applies === "event" ? b.title : undefined,
      dateLine: b.when,
      timeLine: b.time,
      durationLine: b.dur,
      advisor: "Priya Nandakumar",
      ticketLine: doc.applies === "event" ? b.svc : undefined,
      placeName: "Lakeview Central — 2nd floor",
      address: "118 Kingsway, Lakeview LK1 4TR",
      reference: "LKV-8F4210",
      /* The sample customer, for the yourDetails section (2026-09-07). Without
         these the section previewed its own empty state and an author placing
         it could not see what it renders — which is the one thing a preview is
         for. A sample name and address, clearly not a real record. */
      customerName: "Alex Whitfield",
      customerEmail: "a.whitfield@example.com",
      guests: doc.applies === "event" ? ["Ade Ranjit", "Tomas Meyer", "Sofia Kaur"] : undefined,
      answers: [
        { label: "What would you like to discuss?", value: "Fixed-rate options and early repayment" },
        { label: "Are you buying with anyone else?", value: "Yes — joint application" },
      ],
      /* AUTHORED since 2026-09-14 — the sample stands in only while the author
         has written nothing, which is what every other section's sample does.
         The moment a line exists the canvas shows the real list, so what the
         designer draws is what the customer's page will draw. */
      /* The sample's start, unformatted — without it the Date-format setting
         would have nothing to reformat and the preview could not show the
         author what they just picked. Matches the sample dateLine/timeLine
         above; a real booking supplies its own. */
      startISO: "2026-09-04T13:30:00.000Z",
      endISO: "2026-09-04T14:15:00.000Z",
      timezone: "Europe/London",
      prepare: doc.prepare.length ? doc.prepare : ["Photo ID", "Proof of address dated within three months", "Your two most recent payslips"],
      queuePos: "4 of 11",
      queueWait: "18 min",
      promo: { title: "Annual savings review", sub: "30 minutes, no charge" },
      footerLines: ["0800 118 4400 · help@example.edu", "Privacy notice · Cancellation policy"],
    };

    const setupLabel = setupView === "page" ? "Page" : setupView === "branding" ? "Brand" : setupView === "services" ? "Services" : "Design";

    /* ── the three page-scoped panes ───────────────────────────────────
       One pane per scope, opened from the setup row and filling the designer —
       not a tab in the right panel. */
    const pagePane = () => (
      <div style={{ maxWidth: 560 }}>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#5A6070", marginBottom: 5 }}>Page name</div>
          <input value={doc.name} onChange={(e) => patch({ name: e.target.value })} style={INPUT} />
        </div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#5A6070", marginBottom: 5 }}>Applies to</div>
          <div style={{ display: "flex", gap: 5 }}>
            {(["appointment","event","queue"] as CPApplies[]).map((k) => (
              <button key={k} type="button" onClick={() => patch({ applies: k })} disabled={doc.isDefault}
                style={{ flex: 1, height: 32, border: `1px solid ${doc.applies === k ? "#5142C7" : "#D1D4DC"}`, borderRadius: 7, background: doc.applies === k ? "#F6F2FE" : "#FFFFFF", color: doc.isDefault ? "#B5BAC6" : doc.applies === k ? "#5142C7" : "#5A6070", fontSize: 11.5, fontWeight: 600, cursor: doc.isDefault ? "not-allowed" : "pointer", fontFamily: FONT }}>{APPLIES_LABEL[k]}</button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: "#9AA1AE", marginTop: 6, lineHeight: 1.55 }}>Decides which sections the rail offers — Guests means nothing to a queue, a live position means nothing to an appointment.</div>
        </div>
        <div style={{ borderTop: "1px solid #F0F1F4", paddingTop: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#5A6070", marginBottom: 8 }}>Cancellation window</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input value={doc.cancelHours} onChange={(e) => patch({ cancelHours: e.target.value.replace(/[^0-9]/g, "") })} style={{ ...INPUT, width: 64 }} />
            <span style={{ fontSize: 12, color: "#697180" }}>hours before the appointment</span>
          </div>
          <div style={{ fontSize: 11, color: "#9AA1AE", marginTop: 6, lineHeight: 1.55 }}>Inside the window, Manage actions render the closed state instead of the buttons.</div>
        </div>
        {doc.isDefault ? <div style={{ ...NOTE_BLUE, marginTop: 16 }}>This is the tenant default. It cannot be deleted or unassigned — every booking with no matching page renders it.</div> : null}
        <button type="button" disabled={busy} onClick={() => { void save(undefined, `${doc.name} updated.`); }} style={{ ...PRIMARY, marginTop: 16, opacity: busy ? 0.6 : 1 }}>{busy ? "Saving…" : saveActionLabel}</button>
      </div>
    );

    const brandPane = () => (
      <div style={{ maxWidth: 560 }}>
        <div onClick={() => patch({ branding: { ...doc.branding, inherit: !doc.branding.inherit } })} style={{ display: "flex", alignItems: "center", gap: 9, cursor: "pointer", marginBottom: 16 }}>
          <span style={{ width: 32, height: 18, borderRadius: 999, background: doc.branding.inherit ? "#5142C7" : "#D8DBE2", position: "relative", flex: "none" }}>
            <span style={{ position: "absolute", top: 2, left: doc.branding.inherit ? 16 : 2, width: 14, height: 14, borderRadius: 999, background: "#FFFFFF" }} />
          </span>
          <span style={{ minWidth: 0 }}>
            <span style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "#333333" }}>Inherit tenant brand</span>
            <span style={{ display: "block", fontSize: 11, color: "#9AA1AE", lineHeight: 1.5 }}>{doc.branding.inherit ? "Following the tenant brand. Changes there flow through." : "This page keeps its own logo and colours."}</span>
          </span>
        </div>
        <div style={{ opacity: doc.branding.inherit ? 0.45 : 1 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#5A6070", marginBottom: 6 }}>Brand colour</div>
          <div style={{ display: "flex", gap: 7, marginBottom: 16 }}>
            {["#5142C7","#262265","#1B58B8","#1D8348","#B9770E"].map((v) => (
              <span key={v} onClick={() => patch({ branding: { ...doc.branding, brandColor: v, inherit: false } })}
                style={{ width: 30, height: 30, borderRadius: 7, background: v, border: `2px solid ${brand === v ? "#333333" : "transparent"}`, cursor: "pointer", flex: "none" }} />
            ))}
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#5A6070", marginBottom: 6 }}>Page background</div>
          <div style={{ display: "flex", gap: 7 }}>
            {["#FFFFFF","#FAFAF9","#F7F5FF","#F4F7FA"].map((v) => (
              <span key={v} onClick={() => patch({ branding: { ...doc.branding, pageBg: v, inherit: false } })}
                style={{ width: 30, height: 30, borderRadius: 7, background: v, border: `2px solid ${bg === v ? "#333333" : "#E0E2E8"}`, cursor: "pointer", flex: "none" }} />
            ))}
          </div>
        </div>
        <div style={{ ...NOTE_BLUE, marginTop: 16 }}>
          Brand lives here and not on the journey end page: that one takes its journey's theme by operator ruling, so it has no Brand pane at all. It is the one place the two designers legitimately differ.
        </div>
        <button type="button" disabled={busy} onClick={() => { void save(undefined, "Branding updated."); }} style={{ ...PRIMARY, marginTop: 16, opacity: busy ? 0.6 : 1 }}>{busy ? "Saving…" : saveActionLabel}</button>
      </div>
    );

    const saveServices = async () => {
      if (!svcDirty || busy) return;
      setBusy(true); setBusyKind("services");
      try {
        const r = await setPageServices(slug, doc.id, draftClaimed);
        /* The write response is authoritative even when the follow-up list
           refresh fails. Apply it before clearing the local draft so the UI
           never falls back to the pre-write assignment map. */
        setAssignments(r.assignments);
        await reload();
        setSvcDraft(null);
        /* `moved` names the page the SERVER took each service from, which can
           differ from what this list last read if another author claimed it in
           between — so it is reported rather than the local guess. */
        const moved = r.moved.filter((m) => m.fromPageName);
        say("Services updated",
          `${draftClaimed.length} service${draftClaimed.length === 1 ? "" : "s"} now served by this page` +
          (moved.length ? `. ${moved.length} moved from ${[...new Set(moved.map((m) => m.fromPageName))].join(", ")}.` : "."));
      } catch (e) {
        say("Not assigned", errText(e, "The assignments could not be saved. Nothing was changed."));
      } finally { setBusy(false); setBusyKind(null); }
    };

    const servicesPane = () => (
      <div style={{ maxWidth: 620 }}>
        {doc.isDefault ? <div style={{ ...NOTE_BLUE, marginBottom: 14 }}>The default page has no service list. It renders for every booking no other page claims.</div> : null}
        <div style={{ fontSize: 12, color: "#697180", lineHeight: 1.6, marginBottom: 12, maxWidth: 520 }}>
          Claiming a service moves it off whatever page holds it now. A service can only be served by one page, so resolution is never ambiguous.
        </div>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 7, width: "100%", maxWidth: 320, boxSizing: "border-box", height: 32, padding: "0 10px", border: "1px solid #D1D4DC", borderRadius: 7, background: "#FFFFFF", marginBottom: 10 }}>
          <span style={{ color: "#B5BAC6", fontSize: 12 }}>⌕</span>
          <input value={svcQuery} onChange={(e) => setSvcQuery(e.target.value)} placeholder="Find a service" style={{ flex: 1, minWidth: 0, border: "none", outline: "none", fontSize: 12.5, background: "transparent", fontFamily: FONT }} />
        </span>
        {servicesError ? (
          <div role="alert" style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.6 }}>
            <div>{servicesError}</div>
            <button type="button" onClick={() => setServicesAttempt((attempt) => attempt + 1)} style={{ ...PRIMARY, marginTop: 10 }}>Retry</button>
          </div>
        ) : services === null ? (
          <div role="status" style={{ fontSize: 11.5, color: "#9AA1AE" }}>Loading the service catalogue…</div>
        ) : svcList.length === 0 ? (
          <div style={{ fontSize: 11.5, color: "#9AA1AE", lineHeight: 1.6 }}>This tenant's service catalogue is empty, so there is nothing to assign yet.</div>
        ) : matchingServices.length === 0 ? (
          <div role="status" style={{ fontSize: 11.5, color: "#9AA1AE", lineHeight: 1.6 }}>No services match your search.</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 6 }}>
            {matchingServices
              .map((s) => {
                const owner = assignments[s.id];
                const mine = draftClaimed.indexOf(s.id) > -1;
                const other = owner && owner !== doc.id && !mine ? pages.find((p) => p.id === owner) : null;
                return (
                  <button key={s.id} type="button" disabled={busy || doc.isDefault} aria-pressed={mine} onClick={() => {
                    if (doc.isDefault) { say("The default page claims nothing", "It is the fallback. Anything not claimed elsewhere lands here.", "info"); return; }
                    /* Local only. See the note on `svcDraft`: the write happens
                       once, on Save, not once per tick. */
                    setSvcDraft((prev) => {
                      const base = prev || serverClaimed();
                      return mine ? base.filter((x) => x !== s.id) : base.concat([s.id]);
                    });
                  }}
                    style={{ width: "100%", display: "flex", alignItems: "flex-start", gap: 9, padding: "9px 10px", border: `1px solid ${mine ? "#5142C7" : "#E6E8EC"}`, borderRadius: 8, background: mine ? "#F6F2FE" : "#FFFFFF", cursor: busy || doc.isDefault ? "not-allowed" : "pointer", textAlign: "left", fontFamily: FONT, opacity: busy ? 0.65 : 1 }}>
                    <span style={{ width: 15, height: 15, borderRadius: 4, border: `1px solid ${mine ? "#5142C7" : "#C2C7D2"}`, background: mine ? "#5142C7" : "#FFFFFF", color: "#FFFFFF", fontSize: 10, display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none", marginTop: 1 }}>{mine ? "✓" : ""}</span>
                    <span style={{ minWidth: 0, flex: 1 }}>
                      <span style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#333333", lineHeight: 1.4 }}>{s.name}</span>
                      <span style={{ display: "block", fontSize: 10.5, color: mine ? "#5142C7" : other ? "#B9770E" : "#9AA1AE", marginTop: 2, lineHeight: 1.45 }}>
                        {mine ? (serverClaimed().indexOf(s.id) > -1 ? "Assigned to this page" : "Will be claimed on save")
                          : other ? `Claimed by ${other.name}`
                          : serverClaimed().indexOf(s.id) > -1 ? "Will be released on save"
                          : "Falls back to the default page"}
                      </span>
                    </span>
                  </button>
                );
              })}
          </div>
        )}
        {/* ONE write, on demand. The count of pending changes is spelled out
            rather than left to a dot, because "claiming steals" means a save
            here also changes OTHER pages — an author is entitled to see the
            size of that before committing to it. */}
        {doc.isDefault || services === null || servicesError ? null : (
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14, paddingTop: 12, borderTop: "1px solid #F0F1F4", flexWrap: "wrap" }}>
            <button type="button" disabled={!svcDirty || busy} onClick={() => void saveServices()}
              style={{ height: 32, padding: "0 15px", border: "none", borderRadius: 8, background: svcDirty && !busy ? "#5142C7" : "#D9DCE3", color: "#FFFFFF", fontSize: 12.5, fontWeight: 700, cursor: svcDirty && !busy ? "pointer" : "default", fontFamily: FONT, display: "inline-flex", alignItems: "center", gap: 7 }}>
              {busyKind === "services" ? <Spinner size="sm" onColor label="Saving services" /> : null}
              {busyKind === "services" ? "Saving…" : "Save services"}
            </button>
            {svcDirty ? (
              <button type="button" disabled={busy} onClick={() => setSvcDraft(null)}
                style={{ height: 32, padding: "0 12px", border: "1px solid #D1D4DC", borderRadius: 8, background: "#FFFFFF", color: "#5A6070", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: FONT }}>Discard</button>
            ) : null}
            <span style={{ fontSize: 11.5, color: svcDirty ? "#B9770E" : "#9AA1AE", lineHeight: 1.5 }}>
              {svcDirty
                ? [svcAdded.length ? `${svcAdded.length} to claim` : "", svcRemoved.length ? `${svcRemoved.length} to release` : ""].filter(Boolean).join(" · ") + " — not saved yet"
                : `${draftClaimed.length} service${draftClaimed.length === 1 ? "" : "s"} served by this page`}
            </span>
          </div>
        )}
      </div>
    );

    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, fontFamily: FONT, background: "#F4F5F7" }}>
        {/* overflowX auto is the floor; the title block shrinks first. With
            `overflow: hidden` and a `flex: "0 0 auto"` title, Publish sat past
            the right edge at a 924px window with the nav expanded and could not
            be reached at all — the page could not be published. */}
        <div style={{ height: 52, background: "#FFFFFF", borderBottom: "1px solid #E6E8EC", display: "flex", alignItems: "center", gap: 10, padding: "0 14px", flexShrink: 0, overflowX: "auto" }}>
          <button type="button" aria-label="Back to confirmation pages" disabled={busy} onClick={() => {
            if (busy) return;
            if (dirty) {
              say("Page changes not saved", "Save the page changes before returning to the page library.", "info");
              return;
            }
            if (svcDirty) {
              setSetupView("services");
              say("Services not saved", "Save or discard the pending service changes before returning to the page library.", "info");
              return;
            }
            setRoute("library"); void reload();
          }} style={{ width: 28, height: 28, padding: 0, borderRadius: 7, border: "1px solid #D1D4DC", background: "#FFFFFF", color: "#697180", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13, cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1, flex: "none", fontFamily: FONT }}>←</button>
          <span style={{ width: 32, height: 32, borderRadius: 8, background: "#EDE5FE", color: "#5142C7", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 15, flex: "none" }}>▤</span>
          <div style={{ flex: "1 1 auto", minWidth: 60, overflow: "hidden" }}>
            <div style={{ fontSize: 14, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Confirmation Page Designer</div>
            <div style={{ fontSize: 11, color: "#697180", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{doc.name}</div>
          </div>
          {doc.isDefault ? <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: "0.06em", padding: "3px 8px", borderRadius: 999, background: "#E8F1FD", color: "#1B58B8", flex: "none" }}>DEFAULT · FALLBACK</span> : null}
          <span style={{ flex: 1 }} />
          {/* One height for every control in this bar (operator, 2026-09-14).
              The status pill was 26 against everyone else's 30 and the State
              control wrapped a native <select> whose own line-height and
              padding pushed its text off the wrapper's centre — so five
              controls sat on four different optical baselines. Heights are
              equal now and the select is stripped to a bare 30px box. */}
          <span style={{ display: "inline-flex", alignItems: "center", gap: 7, flex: "none", height: 30, boxSizing: "border-box", padding: "0 4px 0 10px", border: "1px solid #D1D4DC", borderRadius: 8, background: "#FFFFFF", fontSize: 12, fontWeight: 600, lineHeight: 1 }}>
            <span style={{ color: "#697180", fontWeight: 500, lineHeight: 1 }}>State</span>
            <select value={pageState} onChange={(e) => setPageState(e.target.value)}
              style={{ height: 28, margin: 0, padding: "0 4px", border: "none", background: "transparent", fontSize: 12, fontWeight: 600, color: "inherit", cursor: "pointer", outline: "none", fontFamily: FONT, lineHeight: "28px" }}>
              {STATES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </span>
          <span title={dirty ? "Edited since it was last saved." : svcDirty ? "Service assignment changes have not been saved." : published ? "Live for the services this page claims." : "Saved, but not published."}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 30, boxSizing: "border-box", padding: "0 12px", borderRadius: 999, background: statusFill, color: statusInk, fontSize: 11.5, fontWeight: 700, flex: "none", whiteSpace: "nowrap", lineHeight: 1 }}>
            <span style={{ width: 6, height: 6, borderRadius: 999, background: statusInk, flex: "none" }} />{statusLabel}
          </span>
          <button type="button" onClick={() => setPreviewOpen(true)}
            style={{ height: 30, boxSizing: "border-box", padding: "0 13px", border: "1px solid #D1D4DC", borderRadius: 8, background: "#FFFFFF", color: "#333333", fontSize: 12, fontWeight: 600, cursor: "pointer", flex: "none", fontFamily: FONT, display: "inline-flex", alignItems: "center", gap: 7, lineHeight: 1 }}>
            {/* An SVG, not ◱. That geometric character has no reliable metrics:
                it rendered at a different size and sat off the text baseline in
                every font that has it at all, and is a blank box in the ones
                that do not. */}
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
              <rect x="1.75" y="2.75" width="12.5" height="10.5" rx="1.75" />
              <path d="M1.75 6h12.5" />
            </svg>
            Preview
          </button>
          {/* Save preserves the document's current status. On a Published page
              that makes the edits live immediately; on a Draft it remains a
              draft. The label says which because the API stores one document,
              not separate live and draft versions. */}
          <button type="button" disabled={busy || !dirty}
            onClick={() => void save(undefined, doc.status === "Published" ? `${doc.name} changes are live.` : `${doc.name} remains a draft.`)}
            style={{ height: 30, boxSizing: "border-box", padding: "0 13px", border: "1px solid #D1D4DC", borderRadius: 8, background: "#FFFFFF", color: dirty ? "#333333" : "#9AA1AE", fontSize: 12, fontWeight: 600, cursor: busy || !dirty ? "default" : "pointer", flex: "none", fontFamily: FONT, display: "inline-flex", alignItems: "center", gap: 7, lineHeight: 1 }}>
            {busyKind === "save" ? <Spinner size="sm" label="Saving" /> : null}{busyKind === "save" ? "Saving…" : dirty ? (doc.status === "Published" ? "Save live" : "Save draft") : "Saved"}
          </button>
          <button type="button" disabled={busy}
            onClick={() => { const next = { ...doc, status: "Published" as const }; void save(next, `${doc.name} is live for the services it claims.`, "publish"); }}
            style={{ height: 30, boxSizing: "border-box", padding: "0 15px", border: "none", borderRadius: 8, background: busy ? "#8C82D8" : "#5142C7", color: "#FFFFFF", fontSize: 12, fontWeight: 700, cursor: busy ? "default" : "pointer", flex: "none", fontFamily: FONT, display: "inline-flex", alignItems: "center", gap: 7, lineHeight: 1 }}>
            {busyKind === "publish" ? <Spinner size="sm" onColor label="Publishing" /> : null}{busyKind === "publish" ? "Publishing…" : "Publish"}
          </button>
        </div>

        {/* Setup row. Design is the three-pane designer; the other three OPEN AS
            PANES — they configure the page, and a page-wide setting shown in the
            panel that is otherwise about the selected section put two scopes in
            one place. The page's own name sits at the right, so the author can
            always see which page they are in. */}
        <div style={{ height: 40, background: "#FFFFFF", borderBottom: "1px solid #E6E8EC", display: "flex", alignItems: "center", gap: 6, padding: "0 14px", flexShrink: 0 }}>
          {/* The leading label the Journey Designer's page strip carries. Without
              it the row opened on a bare "Design" and never said what the chips
              were chips OF. */}
          <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.09em", textTransform: "uppercase", color: "#697180", marginRight: 4, flex: "none" }}>Pages</span>
          {/* Page · Design · Brand · Services (operator, 2026-09-10) — authoring
              order, not alphabetical: what the page IS, then what it looks like,
              then its brand, then which services it serves. Design led before,
              which put the canvas ahead of the decision that determines what the
              canvas can even offer.

              The order is the SAME in both modes. Only the landing tab differs
              (Page for a new page, Design for an existing one) — chips that
              reorder themselves per mode would move the target an author is
              reaching for between one visit and the next. */}
          {([["page", "Page"], [null, "Design"], ["branding", "Brand"], ["services", "Services"]] as const).map(([k, label]) => {
            const on = setupView === k;
            return (
              <button key={label} type="button" onClick={() => setSetupView(k)}
                style={{ height: 28, padding: "0 13px", border: "none", borderRadius: 7, background: on ? "#F6F2FE" : "transparent", color: on ? "#5142C7" : "#5A6070", fontSize: 12.5, fontWeight: on ? 700 : 600, cursor: "pointer", fontFamily: FONT }}>{label}</button>
            );
          })}
          <span style={{ flex: 1 }} />
          <span style={{ display: "inline-flex", alignItems: "center", gap: 7, minWidth: 0 }}>
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: "#B5BAC6" }}>Page</span>
            <span style={{ fontSize: 11.5, fontWeight: 700, color: "#5A6070", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 260 }}>{doc.name}</span>
            <span style={{ fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: APPLIES_TINT[doc.applies].tint, color: APPLIES_TINT[doc.applies].color, flex: "none" }}>{APPLIES_LABEL[doc.applies]}</span>
          </span>
        </div>

        {setupView ? (
          <div style={{ flex: 1, overflow: "auto", minHeight: 0, background: "#FFFFFF" }}>
            <div style={{ padding: "20px 24px 28px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 16 }}>
                <span onClick={() => setSetupView(null)} style={{ width: 26, height: 26, borderRadius: 7, border: "1px solid #D1D4DC", color: "#697180", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, cursor: "pointer", flex: "none" }}>←</span>
                <span>
                  <span style={{ display: "block", fontSize: 15, fontWeight: 700 }}>{setupLabel}</span>
                  <span style={{ display: "block", fontSize: 11.5, color: "#9AA1AE", marginTop: 1 }}>
                    {setupView === "page" ? "Name, what it applies to, and the cancellation window."
                      : setupView === "branding" ? "Logo and colours for this page."
                      : "Which services this page serves."}
                  </span>
                </span>
              </div>
              {setupView === "page" ? pagePane() : setupView === "branding" ? brandPane() : servicesPane()}
            </div>
          </div>
        ) : (
        <div ref={rowRefCb} style={{ flex: 1, display: "flex", minHeight: 0, position: "relative",
          /* Load-bearing, not tidiness. The editor is an absolutely-positioned
             child parked at translateX(100%) when closed, and a translated
             abs-positioned child STILL counts toward its ancestor's
             scrollWidth — so the closed panel added its own width (260px at the
             narrow size) to the designer's scroll box and put a horizontal
             scrollbar under the canvas leading into empty space. Clipping the
             parked panel keeps the slide transition and removes the phantom
             scroll; the canvas keeps its own overflow, so nothing that should
             scroll stops scrolling. */
          overflow: "hidden" }}>
          {/* Left rail — the SHARED CSRail, with the blocks rail passed as its
              children so both surfaces get one list order and one heading. */}
          <div style={{ width: 212, flex: "none", background: "#FFFFFF", borderRight: "1px solid #E6E8EC", overflow: "auto", padding: 14, minHeight: 0 }}>
            <CSRail
            applicableTypes={csScope}
              state={state}
              type={csType}
              surface={CP_SURFACE}
              sel={selSection}
              onSelect={(id) => { setSelSection(id); if (id) setSelBlock(null); }}
              onChange={setSections}
            >
              {/* The Journey Designer's palette, verbatim: a two-column grid of
                  DRAGGABLE labels with a ✓ when the type is already on the page.
                  Drag only — a click that appended to the bottom is how a block
                  ends up somewhere nobody chose, and removal is the × on the
                  block itself. Mine was a vertical list of dashed "+" rows, which
                  looked nothing like it and behaved differently. */}
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.09em", textTransform: "uppercase", color: "#697180", marginBottom: 8 }}>Blocks</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 18 }}>
                {BLOCK_IDS.map((k) => {
                  const mine = blocks.filter((x) => x.type === k);
                  const on = mine.length > 0;
                  /* Checkbox semantics, matching the Journey Designer's palette:
                     unchecking takes off the LAST block of the type, never all of
                     them, and the count says how many are there. */
                  const toggle = () => {
                    if (!on) {
                      const nb = mkBlock(k);
                      patch({ content: blocks.concat([nb]) });
                      setSelBlock(nb.uid); setSelSection(null);
                      return;
                    }
                    const last = mine[mine.length - 1]!;
                    patch({ content: blocks.filter((x) => x.uid !== last.uid) });
                    if (selBlock === last.uid) setSelBlock(null);
                  };
                  return (
                    <button key={k}
                      draggable
                      onClick={toggle}
                      onDragStart={(e) => { e.dataTransfer?.setData(BLOCK_ADD_DND, k); if (e.dataTransfer) e.dataTransfer.effectAllowed = "copy"; }}
                      title={on
                        ? `${BLOCK_META[k].label} · ${mine.length} on this page. Click to take the last one off, or drag to add another where you choose.`
                        : `${BLOCK_META[k].hint} Click to add it at the end, or drag onto the page.`}
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 8px", border: `1px solid ${on ? "#5142C7" : "#E6E8EC"}`, background: on ? "#F6F2FE" : "#FFFFFF", color: on ? "#5142C7" : "#333333", borderRadius: 7, fontSize: 11.5, fontWeight: 600, cursor: "pointer", fontFamily: FONT, textAlign: "left" }}>
                      {/* Same three columns as a section row (operator,
                          2026-09-14): box, 20px tile, label. Without the tile
                          the block labels started 27px left of the section
                          labels directly above them, so one rail had two left
                          edges. The glyph was already in BLOCK_META and was
                          only being shown in the canvas. */}
                      <span style={{ width: 16, height: 16, borderRadius: 5, border: `1.5px solid ${on ? "#5142C7" : "#C9CDD6"}`, background: on ? "#5142C7" : "#FFFFFF", color: "#FFFFFF", fontSize: 10, fontWeight: 900, lineHeight: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none" }}>{on ? "✓" : ""}</span>
                      <span aria-hidden="true" style={{ width: 20, height: 20, borderRadius: 6, background: "#F1F2F5", color: "#697180", fontSize: 10, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none" }}>{BLOCK_META[k].glyph}</span>
                      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{BLOCK_META[k].label}</span>
                      {mine.length > 1 ? <span style={{ fontSize: 9.5, fontWeight: 800, color: "#5142C7", flex: "none", fontFamily: "ui-monospace, monospace" }}>{mine.length}</span> : null}
                    </button>
                  );
                })}
              </div>
            </CSRail>
          </div>

          {/* canvas — the SHARED CSCanvas */}
          <div style={{ flex: 1, minWidth: 0, overflow: "auto", background: "#F4F5F7", padding: "18px 20px 30px", paddingRight: inspOpen && reserveInsp ? INSP_MAX + 20 : 20, position: "relative", display: "flex", flexDirection: "column" }}>
            {/* The hint rides the width bar only when the bar has room for it on
                one line. On a narrow canvas it was the fourth wrapped line of
                chrome above the preview. */}
            <CanvasWidthBar width={pageWidth} presets={WIDTH_PRESETS} extra={reserveInsp || !inspOpen ? (
              <span style={{ fontSize: 11, color: "#9AA1AE" }}>· {stateMeta.hint} · design preview, not a live booking</span>
            ) : null} />
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "center", flex: 1 }}>
              <CanvasEdge width={pageWidth} side={-1} />
              <CanvasColumn width={pageWidth}>
                <div style={{ background: bg, border: "1px solid #E0E2E8", borderRadius: 14, padding: "14px 0", boxShadow: "0 2px 10px rgba(38,34,101,0.06)" }}>
                  <div style={{ padding: "0 14px" }}>
                    <CSCanvas
                      state={state}
                      type={csType}
                      surface={CP_SURFACE}
                      booking={csBooking}
                      fields={{ bannerTitle: doc.bannerTitle, bannerSub: doc.bannerSub, cancelHours: doc.cancelHours, note: doc.note, rebookHours: doc.rebookHours, checkinCodeTarget: doc.checkinCodeTarget, downloadsOff: doc.downloadsOff, prepare: doc.prepare, summaryOff: doc.summaryOff, summaryLabels: doc.summaryLabels, summaryOrder: doc.summaryOrder, summaryDateFormat: doc.summaryDateFormat, summaryTimeFormat: doc.summaryTimeFormat }}
                      sel={selSection}
                      onSelect={(id) => { setSelSection(id); if (id) setSelBlock(null); }}
                      onChange={setSections}
                      pageState={pageState as CSPageState}
                      accent={brand}
                    />
                  </div>
                  {/* Author-typed blocks, through the SAME canvas the journey
                      designer's blocks use — so the drop geometry, the insertion
                      line, the drag-to-reorder and the per-block ↑ ↓ × are one
                      implementation rather than two that nearly agree. Blocks
                      render after the sections: the booking is what the customer
                      came for, so copy about it reads as a footnote. */}
                  <div style={{ marginTop: 10 }}>
                    <BlockCanvas
                      blocks={blocks.map((b) => ({ ...b, id: b.uid }))}
                      sel={selBlock}
                      onSelect={(id) => { setSelBlock(id); if (id) setSelSection(null); }}
                      onMove={(from, to) => {
                        const next = blocks.slice();
                        const [x] = next.splice(from, 1);
                        next.splice(to, 0, x!);
                        patch({ content: next });
                      }}
                      onInsert={(type, at) => {
                        const nb = mkBlock(type);
                        const next = blocks.slice();
                        next.splice(Math.max(0, Math.min(at, next.length)), 0, nb);
                        patch({ content: next });
                        setSelBlock(nb.uid); setSelSection(null);
                      }}
                      onRemove={(uid) => {
                        patch({ content: blocks.filter((x) => x.uid !== uid) });
                        if (selBlock === uid) setSelBlock(null);
                      }}
                      labelOf={(b) => BLOCK_META[b.type]?.label ?? b.type}
                      emptyState={
                        <div style={{ border: "1px dashed #D1D4DC", borderRadius: 9, padding: "12px 11px", fontSize: 11, lineHeight: 1.5, color: "#697180" }}>
                          Click or drag a block from the palette for anything the booking record cannot say.
                        </div>
                      }
                      renderBlock={(b) => {
                        const block = b as unknown as CPBlock;
                        const txt = String(block.text ?? "");
                        const ty = block.type;
                        const href = ty === "link" ? confirmationBlockHref(block) : null;
                        return (
                          <div style={{ padding: "11px 12px", borderRadius: 10, border: "1px solid #EDEEF2", background: "#FFFFFF" }}>
                            {ty === "heading"
                              ? <div style={{ fontSize: 15, fontWeight: 700, color: "#14161E", letterSpacing: "-0.01em" }}>{txt}</div>
                              : ty === "callout"
                                ? <div style={{ padding: 14, borderRadius: 10, background: "#F6F2FE", border: "1px solid #DCCCFC", fontSize: 12.5, color: "#4B4285", lineHeight: 1.65 }}>{txt}</div>
                                : ty === "link"
                                  ? txt.trim() ? (href
                                    ? <span style={{ fontSize: 13, fontWeight: 700, color: brand, borderBottom: `1px solid ${brand}` }}>{txt.trim()}</span>
                                    : <span style={{ fontSize: 13, color: "#3A3F4B" }}>{txt}</span>) : null
                                  : ty === "divider"
                                    ? <div style={{ height: 1, background: "#E6E8EC" }} />
                                    : <div style={{ fontSize: 13, color: "#4B5563", lineHeight: 1.7 }}>{txt}</div>}
                          </div>
                        );
                      }}
                    />
                  </div>
                </div>
              </CanvasColumn>
              <CanvasEdge width={pageWidth} side={1} />
            </div>
          </div>

          {/* Editor — an overlay over the canvas, minimizable, exactly as the
              Journey Designer's is. Only ever shows what is SELECTED: sections
              use the shared CSInspector; blocks are this surface's own, because
              the journey designer's blocks are a different registry. */}
          <div style={{
            position: "absolute", top: 0, right: 0, bottom: 0, zIndex: 20,
            width: inspW, maxWidth: "92%",
            background: "#FFFFFF", borderLeft: "1px solid #E6E8EC",
            boxShadow: "-10px 0 28px rgba(20,19,31,0.10)",
            display: "flex", flexDirection: "column", minHeight: 0,
            transform: inspOpen ? "translateX(0)" : "translateX(100%)",
            transition: "transform 170ms ease",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 14px 10px", borderBottom: "1px solid #E6E8EC", flexShrink: 0 }}>
              <div style={{ ...CAPS, flex: 1 }}>{selBlockDoc ? "Block" : selRow ? "Section" : "Nothing selected"}</div>
              <button onClick={() => setInspOpen(false)} title="Minimize the editor"
                style={{ width: 26, height: 26, border: "1px solid #D1D4DC", borderRadius: 6, background: "#FFFFFF", color: "#697180", fontSize: 13, lineHeight: 1, cursor: "pointer", fontFamily: FONT, flex: "none" }}>›</button>
            </div>
            <div style={{ flex: 1, overflow: "auto", padding: 14, minHeight: 0 }}>
              {selBlockDoc ? (
                <>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 3 }}>{BLOCK_META[selBlockDoc.type]?.label ?? `Unknown block · ${selBlockDoc.type}`}</div>
                  <div style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.55, marginBottom: 14 }}>
                    {BLOCK_META[selBlockDoc.type]?.hint ?? "This block's type is not one this designer knows. It is kept exactly as written and rendered as text — never dropped."}
                  </div>
                  {/* WHERE ON THE PAGE (operator, 2026-09-14). The arrows on the
                      canvas reorder blocks among themselves; this is what puts
                      one BETWEEN two sections, which is what they were actually
                      for. A named slot rather than a drag: the sections are a
                      short list with real names, and "before Downloads" is a
                      sentence an author can check against the preview. */}
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Place on the page</div>
                    <select
                      value={String(Math.max(0, Math.min(typeof selBlockDoc.at === "number" ? selBlockDoc.at : doc.sections.length, doc.sections.length)))}
                      onChange={(e) => patchBlock(selBlockDoc.uid, { at: Number(e.target.value) })}
                      style={{ ...INPUT, fontSize: 12 }}>
                      {doc.sections.map((r, i) => {
                        const d = csDef(r.type);
                        return <option key={r.uid} value={String(i)}>{`Before ${d ? d.label : r.type}`}</option>;
                      })}
                      <option value={String(doc.sections.length)}>At the end of the page</option>
                    </select>
                  </div>
                  {selBlockDoc.type === "divider" ? (
                    <div style={{ fontSize: 11.5, color: "#9AA1AE", lineHeight: 1.6 }}>A divider has nothing to configure.</div>
                  ) : selBlockDoc.type === "link" ? (
                    <>
                      <div style={{ marginBottom: 12 }}>
                        <label htmlFor={`cp-link-label-${selectedBlockKey}`} style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#5A6070", marginBottom: 5 }}>Link label</label>
                        <input id={`cp-link-label-${selectedBlockKey}`} type="text" value={String(selBlockDoc.text ?? "")}
                          aria-invalid={selectedLinkIssue?.field === "label" || undefined}
                          aria-describedby={selectedLinkIssue?.field === "label" ? `cp-link-label-error-${selectedBlockKey}` : undefined}
                          onChange={(e) => patchBlock(selBlockDoc.uid, {
                            text: e.target.value,
                            ...(!ownsHref(selBlockDoc) && linkDestination(selBlockDoc) ? { href: linkDestination(selBlockDoc) } : {}),
                          })}
                          style={{ ...INPUT, height: 34 }} />
                        {selectedLinkIssue?.field === "label" ? (
                          <div id={`cp-link-label-error-${selectedBlockKey}`} role="alert" style={{ marginTop: 5, fontSize: 11, color: "#C0392B", lineHeight: 1.45 }}>{selectedLinkIssue.message}</div>
                        ) : null}
                      </div>
                      <div style={{ marginBottom: 12 }}>
                        <label htmlFor={`cp-link-href-${selectedBlockKey}`} style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#5A6070", marginBottom: 5 }}>Destination URL</label>
                        <input id={`cp-link-href-${selectedBlockKey}`} type="url" value={linkDestination(selBlockDoc)}
                          aria-invalid={selectedLinkIssue?.field === "href" || undefined}
                          aria-describedby={`cp-link-href-help-${selectedBlockKey}${selectedLinkIssue?.field === "href" ? ` cp-link-href-error-${selectedBlockKey}` : ""}`}
                          onChange={(e) => patchBlock(selBlockDoc.uid, { href: e.target.value })}
                          placeholder="https://example.com/checklist" style={{ ...INPUT, height: 34 }} />
                        <div id={`cp-link-href-help-${selectedBlockKey}`} style={{ marginTop: 5, fontSize: 10.5, color: "#697180", lineHeight: 1.45 }}>Use a full URL beginning with http:// or https://.</div>
                        {selectedLinkIssue?.field === "href" ? (
                          <div id={`cp-link-href-error-${selectedBlockKey}`} role="alert" style={{ marginTop: 4, fontSize: 11, color: "#C0392B", lineHeight: 1.45 }}>{selectedLinkIssue.message}</div>
                        ) : null}
                      </div>
                    </>
                  ) : (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#5A6070", marginBottom: 5 }}>Text</div>
                      <textarea value={String(selBlockDoc.text ?? "")} onChange={(e) => patchBlock(selBlockDoc.uid, { text: e.target.value })} rows={selBlockDoc.type === "heading" ? 2 : 5}
                        style={{ ...INPUT, height: "auto", padding: "8px 10px", lineHeight: 1.5, resize: "vertical" }} />
                    </div>
                  )}
                  <button type="button" disabled={busy} onClick={() => { void save(undefined, "Blocks updated."); }} style={{ ...PRIMARY, width: "100%", marginTop: 2, opacity: busy ? 0.6 : 1 }}>{busy ? "Saving…" : saveActionLabel}</button>
                </>
              ) : selRow ? (
                <>
                  <CSInspector
                    id={selRow.type}
                    type={csType}
                    applicableTypes={csScope}
                    fields={{ bannerTitle: doc.bannerTitle, bannerSub: doc.bannerSub, cancelHours: doc.cancelHours, note: doc.note, rebookHours: doc.rebookHours, checkinCodeTarget: doc.checkinCodeTarget, prepare: doc.prepare, summaryOff: doc.summaryOff, summaryLabels: doc.summaryLabels, summaryOrder: doc.summaryOrder, summaryDateFormat: doc.summaryDateFormat, summaryTimeFormat: doc.summaryTimeFormat }}
                    onPatch={(pf) => patch(pf as Partial<CPDoc>)}
                    hidden={!!selRow.hidden}
                    onToggle={() => setSections(csToggle(state, selRow.uid))}
                  />
                  {selRow.type === "downloads" ? (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #F4F5F7" }}>
                      {/* ONE ROW PER ITEM, TOGGLE FIRST (operator, 2026-09-14).
                          It used to print the item name and then a toggle whose
                          own label said "Shown"/"Hidden" — the toggle already
                          says that, twice as fast, and the word only made four
                          items take four times the height. Reading down the
                          switches now answers "what does this page offer" at a
                          glance, in the order the page draws them.

                          THE KEYS ARE POSITIONAL AND FROZEN. `dl0/1/2` were
                          stored by index against Calendar/Wallet/PDF; the list
                          is displayed in page order and Data export appended as
                          `dl3`, so no stored value changes meaning. */}
                      {([
                        { key: "dl0", id: "ics", label: "Calendar file (.ics)" },
                        { key: "dl2", id: "pdf", label: "PDF summary" },
                        { key: "dl3", id: "export", label: "Data export" },
                        { key: "dl1", id: "wallet", label: "Apple Wallet pass" },
                      ]).map((it) => {
                        const on = (doc.downloadsOff ?? []).indexOf(it.id) < 0;
                        const flip = () => patch({ downloadsOff: on
                          ? [...(doc.downloadsOff ?? []), it.id]
                          : (doc.downloadsOff ?? []).filter((x) => x !== it.id) } as Partial<CPDoc>);
                        return (
                          <div key={it.key} onClick={flip} role="switch" aria-checked={on} aria-label={it.label}
                            style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", padding: "6px 0" }}>
                            <span style={{ width: 32, height: 18, borderRadius: 999, background: on ? "#5142C7" : "#D8DBE2", position: "relative", flex: "none" }}>
                              <span style={{ position: "absolute", top: 2, left: on ? 16 : 2, width: 14, height: 14, borderRadius: 999, background: "#FFFFFF" }} />
                            </span>
                            <span style={{ fontSize: 12, fontWeight: 600, color: on ? "#333333" : "#8A90A0" }}>{it.label}</span>
                          </div>
                        );
                      })}
                      <div style={{ fontSize: 10.5, color: "#B5BAC6", lineHeight: 1.5, marginTop: 6 }}>Switching one off withholds it. It cannot add one the server does not offer — the Wallet pass is not built yet; the PDF is live at the manage summary op.</div>
                    </div>
                  ) : null}
                  <button type="button" disabled={busy} onClick={() => { void save(undefined, `${doc.name} updated.`); }} style={{ ...PRIMARY, width: "100%", marginTop: 14, opacity: busy ? 0.6 : 1 }}>{busy ? "Saving…" : saveActionLabel}</button>
                </>
              ) : (
                <div style={{ ...NOTE_BLUE }}>
                  This page is built from <b>sections</b>, not settings — pick one on the left to see what it renders and what you can set. Blocks you add sit below them. Page, Brand and Services are along the top.
                </div>
              )}
            </div>
          </div>

          {/* Reopen tab. Deliberately NOT re-opened by the next click on the
              canvas — a panel that springs back is the behaviour that made
              someone close it. */}
          {!inspOpen ? (
            <button onClick={() => setInspOpen(true)} title="Open the editor"
              style={{ position: "absolute", top: 14, right: 0, zIndex: 19, height: 34, padding: "0 12px 0 10px", border: "1px solid #E6E8EC", borderRight: "none", borderRadius: "8px 0 0 8px", background: "#FFFFFF", color: "#5142C7", fontSize: 12, fontWeight: 700, cursor: "pointer", boxShadow: "-4px 0 14px rgba(20,19,31,0.08)", fontFamily: FONT }}>‹ Editor</button>
          ) : null}
        </div>
        )}
      </div>
    );
  }

  return (
    /* Its own rail section, and NO tab bar (2026-09-09). Manage Pages was
       promoted out of the Journeys tab strip into a module of its own, so the
       strip is not its chrome any more: a "Journeys" tab across the top of this
       screen names a section the screen no longer belongs to, and clicking it
       leaves. The rail entry is where the section is named now. */
    <Screen rail="manage-pages">
      {previewOpen && doc ? <ConfirmationPagePreview doc={doc} onClose={() => setPreviewOpen(false)} /> : null}
      {/* CALLED, not mounted as <Library />: an inner function component is a NEW
          component type on every render, so React would remount the whole subtree
          on each keystroke and every text field would lose focus. */}
      {route === "library" ? Library() : Designer()}
    </Screen>
  );
}
